import React, { useCallback } from 'react';
import {
  createPlaceRequest,
  popAdminPanel,
  setPlaceDescription,
  setPlaceLocation,
  setPlaceName,
  setPlacePageId,
  setPlacePageTitle,
  setPlacePosterImages,
  setPlaceTags,
  updatePlaceRequest,
} from '../../../actions';
import {
  getPlaceDescription,
  getPlaceLocation,
  getPlaceName,
  getPlacePageId,
  getPlacePageTitle,
  getPlacePosterImage,
  getPlaceTags,
  placeHasId,
} from '../../../selectors';
import { useDispatch, useSelector } from 'react-redux';
import { PosterImageType } from '../../../state/types/poster-image';

export const useAdminPlacePanelController = (placeKey: string) => {
  const dispatch = useDispatch();

  const name = useSelector(getPlaceName(placeKey));
  const description = useSelector(getPlaceDescription(placeKey));
  const editingPlace = useSelector(placeHasId(placeKey));
  const tags = useSelector(getPlaceTags(placeKey));
  const pageId = useSelector(getPlacePageId(placeKey));
  const pageTitle = useSelector(getPlacePageTitle(placeKey));

  const { address } = useSelector(getPlaceLocation(placeKey));

  const { streetAddress = '', region = '', postalCode = '', locality = '' } =
    address || {};

  const posterImage = useSelector(getPlacePosterImage(placeKey));
  const posterImageKey = posterImage ? posterImage.imageKey : undefined;

  const onNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setPlaceName(placeKey, e.target.value));
    },
    [placeKey],
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setPlaceDescription(placeKey, e.target.value));
    },
    [placeKey],
  );

  const onTagsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setPlaceTags(placeKey, e.target.value));
    },
    [placeKey],
  );

  const onStreetAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(
        setPlaceLocation(placeKey, {
          streetAddress: e.target.value,
          region,
          postalCode,
          locality,
        }),
      );
    },
    [region, postalCode, locality, placeKey],
  );

  const onLocalityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setPlaceLocation(placeKey, {
          streetAddress,
          region,
          postalCode,
          locality: e.target.value,
        }),
      );
    },
    [streetAddress, region, postalCode, placeKey],
  );

  const onPostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setPlaceLocation(placeKey, {
          streetAddress,
          region,
          postalCode: e.target.value,
          locality,
        }),
      );
    },
    [streetAddress, region, locality, placeKey],
  );

  const onRegionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setPlaceLocation(placeKey, {
          streetAddress,
          region: e.target.value,
          postalCode,
          locality,
        }),
      );
    },
    [streetAddress, postalCode, locality, placeKey],
  );

  const onSaveClick = useCallback(() => {
    if (editingPlace) {
      dispatch(updatePlaceRequest(placeKey));
    } else {
      dispatch(createPlaceRequest(placeKey));
    }
    dispatch(popAdminPanel());
  }, [placeKey, editingPlace]);

  const onDiscardClick = useCallback(() => {
    dispatch(popAdminPanel());
  }, [placeKey]);

  const onPosterImageUploadStart = useCallback(
    (imageKey: string) => {
      dispatch(
        setPlacePosterImages(placeKey, [
          { type: PosterImageType.Generic, imageKey },
        ]),
      );
    },
    [placeKey],
  );

  const onPageSelect = useCallback(
    (page: { id: string; title: string }) => {
      dispatch(setPlacePageId(placeKey, page.id));
      dispatch(setPlacePageTitle(placeKey, page.title));
    },
    [placeKey],
  );

  return {
    description,
    editingPlace,
    locality,
    name,
    onDescriptionChange,
    onDiscardClick,
    onLocalityChange,
    onNameChange,
    onPageSelect,
    onPostalCodeChange,
    onPosterImageUploadStart,
    onRegionChange,
    onSaveClick,
    onStreetAddressChange,
    onTagsChange,
    pageId,
    pageTitle,
    postalCode,
    posterImageKey,
    region,
    streetAddress,
    tags,
  };
};
